import {
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import type {
  NotificationActionDto,
  NotificationDto,
  NotificationDtoSeverity,
} from '@/generated/api/pacApi.schemas.ts';
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getNotificationsControllerGetNotificationsQueryKey,
  getNotificationsControllerMarkNotificationAsReadMutationOptions,
} from '@/generated/api/notifications.ts';
import ReactMarkdown from 'react-markdown';
import useNotificationActionHandler, {
  canRunNotificationActionWithoutHref,
} from '@/common/notifications/useNotificationActionHandler.ts';
import { useProductAnalytics } from '@/common/analytics/product-analytics.tsx';
import { PRODUCT_ANALYTICS_EVENT } from '@/common/analytics/events.ts';

type NotificationSplitViewProps = {
  notifications: NotificationDto[];
  height?: number | string;
  listWidth?: number | string;
  autoSelectFirst?: boolean;
  emptyLabel?: string;
  onMarkedRead?: (id: string) => void;
};

const severityColor: Record<NotificationDtoSeverity, string> = {
  info: 'blue',
  success: 'green',
  warning: 'yellow',
  error: 'red',
};

export default function NotificationSplitView({
  notifications,
  height = 400,
  listWidth = 280,
  autoSelectFirst = false,
  emptyLabel = 'No notifications yet.',
  onMarkedRead,
}: NotificationSplitViewProps) {
  const queryClient = useQueryClient();
  const actionHandler = useNotificationActionHandler();
  const capture = useProductAnalytics();

  const markReadMutation = useMutation(
    getNotificationsControllerMarkNotificationAsReadMutationOptions({
      mutation: {
        onSettled: () => {
          void queryClient.invalidateQueries({
            queryKey: getNotificationsControllerGetNotificationsQueryKey(),
          });
        },
      },
    }),
  );

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected =
    notifications.find((item) => item.recipientId === selectedId) ?? null;

  useEffect(() => {
    if (notifications.length === 0) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }

    if (
      selectedId !== null &&
      notifications.some((item) => item.recipientId === selectedId)
    ) {
      return;
    }

    if (autoSelectFirst) {
      setSelectedId(notifications[0].recipientId);
      return;
    }

    if (selectedId !== null) setSelectedId(null);
  }, [autoSelectFirst, notifications, selectedId]);

  const markReadAndSelect = (id: string) => {
    const notification = notifications.find((item) => item.recipientId === id);
    if (notification) {
      capture(PRODUCT_ANALYTICS_EVENT.notificationOpened, {
        severity: notification.severity ?? 'info',
        was_unread: notification.readAt === null,
        has_actions: (notification.actions?.length ?? 0) > 0,
      });
    }
    setSelectedId(id);
    markReadMutation.mutate({ notificationId: id });
    onMarkedRead?.(id);
  };

  return notifications.length === 0 ? (
    <Box p="md">
      <Text size="sm" c="dimmed">
        {emptyLabel}
      </Text>
    </Box>
  ) : (
    <Group
      h={height}
      align="stretch"
      gap={0}
      wrap="nowrap"
      style={{ minHeight: 0 }}
    >
      <ScrollArea
        h="100%"
        w={listWidth}
        style={{ flex: '0 0 auto', minHeight: 0 }}
      >
        <Stack gap={0}>
          {notifications.map((item, index) => {
            const isSelected = selected?.recipientId === item.recipientId;
            const isUnread = item.readAt === null;
            const severity = item.severity ?? 'info';
            const color = severityColor[severity];
            const displayTitle = stripAlertPrefix(item.subject);
            const preview = getPreview(item);

            return (
              <UnstyledButton
                key={getNotificationKey(item, index)}
                onClick={() => markReadAndSelect(item.recipientId)}
                p="sm"
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  background: isSelected
                    ? 'var(--mantine-color-gray-1)'
                    : 'transparent',
                  borderBottom: '1px solid var(--mantine-color-gray-2)',
                  boxShadow: isSelected
                    ? `inset 0 0 0 1px var(--mantine-color-${color}-5)`
                    : undefined,
                }}
              >
                <Group
                  justify="space-between"
                  align="flex-start"
                  gap="xs"
                  wrap="nowrap"
                >
                  <Box style={{ minWidth: 0, flex: 1 }}>
                    <Group gap={6} wrap="nowrap">
                      {isUnread && (
                        <Box
                          aria-label="Unread"
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: 999,
                            flex: '0 0 auto',
                            background: `var(--mantine-color-${color}-6)`,
                          }}
                        />
                      )}

                      <Text
                        size="sm"
                        fw={isUnread ? 700 : 500}
                        lineClamp={1}
                        style={{ minWidth: 0 }}
                      >
                        {displayTitle}
                      </Text>
                    </Group>

                    {preview && (
                      <Text size="xs" c="dimmed" lineClamp={1} mt={4}>
                        {preview}
                      </Text>
                    )}

                    <Text size="xs" c="dimmed" mt={4}>
                      {formatDate(item.createdAt)}
                    </Text>
                  </Box>

                  {item.severity && item.severity !== 'info' && (
                    <Badge size="xs" variant="light" color={color}>
                      {item.severity}
                    </Badge>
                  )}
                </Group>
              </UnstyledButton>
            );
          })}
        </Stack>
      </ScrollArea>

      <Divider orientation="vertical" />

      <Box
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {selected ? (
          <>
            <Paper
              p="md"
              radius={0}
              shadow="none"
              withBorder={false}
              style={{ flexShrink: 0 }}
            >
              <Group
                justify="space-between"
                align="flex-start"
                gap="md"
                wrap="nowrap"
              >
                <Box style={{ minWidth: 0 }}>
                  <Group gap="xs" mb={4}>
                    {selected.severity && selected.severity !== 'info' && (
                      <Badge
                        size="sm"
                        variant="light"
                        color={severityColor[selected.severity]}
                      >
                        {selected.severity}
                      </Badge>
                    )}

                    <Text size="xs" c="dimmed">
                      {formatDate(selected.createdAt)}
                    </Text>
                  </Group>

                  <Title order={5} lineClamp={2}>
                    {stripAlertPrefix(selected.subject)}
                  </Title>

                  {selected.subtitle && (
                    <Text size="sm" c="dimmed" mt={4} lineClamp={2}>
                      {selected.subtitle}
                    </Text>
                  )}
                </Box>
              </Group>
            </Paper>

            <Divider />

            <ScrollArea style={{ flex: 1, minHeight: 0 }}>
              <Paper p="md" radius={0} shadow="none">
                <Box className="notification-markdown-body">
                  <ReactMarkdown>
                    {stripDuplicateMarkdownTitle(
                      selected.body,
                      selected.subject,
                    )}
                  </ReactMarkdown>
                </Box>
              </Paper>
            </ScrollArea>

            {selected.actions && selected.actions.length > 0 && (
              <>
                <Divider />

                <Group justify="flex-end" p="sm">
                  {selected.actions.map((action) => (
                    <Button
                      key={action.actionId ?? action.label}
                      size="xs"
                      variant={getPrimaryActionVariant(action)}
                      color={action.variant === 'danger' ? 'red' : undefined}
                      disabled={
                        !action.href &&
                        !canRunNotificationActionWithoutHref(action.actionId)
                      }
                      onClick={() => {
                        capture(PRODUCT_ANALYTICS_EVENT.notificationActionUsed, {
                          action_id: action.actionId,
                          severity: selected.severity ?? 'info',
                          has_destination: Boolean(action.href),
                        });
                        actionHandler(action.actionId, action.href);
                      }}
                    >
                      {action.label}
                    </Button>
                  ))}
                </Group>
              </>
            )}
          </>
        ) : (
          <Box p="md">
            <Text size="sm" c="dimmed">
              Select a notification.
            </Text>
          </Box>
        )}
      </Box>
    </Group>
  );
}

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function stripAlertPrefix(value: string) {
  return value.replace(/^ALERT:\s*/i, '').trim();
}

function stripDuplicateMarkdownTitle(body: string, subject: string) {
  const cleanSubject = stripAlertPrefix(subject).toLowerCase();

  return body
    .replace(new RegExp(`^#{1,6}\\s*${escapeRegExp(subject)}\\s*\\n+`, 'i'), '')
    .replace(
      new RegExp(`^\\*\\*${escapeRegExp(subject)}\\*\\*\\s*\\n+`, 'i'),
      '',
    )
    .replace(
      new RegExp(
        `^\\*\\*ALERT:\\*\\*\\s*${escapeRegExp(cleanSubject)}\\s*\\n+`,
        'i',
      ),
      '',
    )
    .replace(
      new RegExp(
        `^\\*\\*ALERT:\\*\\*\\s*${escapeRegExp(stripAlertPrefix(subject))}\\s*\\n+`,
        'i',
      ),
      '',
    )
    .trim();
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function getNotificationKey(item: NotificationDto, index: number) {
  return item.recipientId ?? `${item.recipientId}-${item.createdAt}-${index}`;
}

function getPreview(item: NotificationDto) {
  if (item.subtitle?.trim()) return item.subtitle.trim();

  return (
    item.body
      .replace(/[#*_`>-]/g, '')
      .split('\n')
      .map((line) => line.trim())
      .find((line) => line && !line.toLowerCase().startsWith('alert:')) ?? ''
  );
}

function getPrimaryActionVariant(action: NotificationActionDto) {
  if (action.variant === 'danger') return 'outline';
  if (action.variant === 'secondary') return 'default';
  return 'filled';
}
